/**
 * Pure helpers for per-invoice upload eligibility.
 *
 * Once an invoice has moved past the review/matching stage and into downstream
 * accounting (D365), re-uploading an invoice copy would produce a mismatch
 * between what the system recorded and what accounts-payable processed. The
 * statuses below mark those terminal or near-terminal points.
 *
 * "approved" (lowercase) is the legacy v1 invoice_status.state value — kept
 * for backwards compatibility with old invoices that have not been migrated to
 * the v2 lifecycle model.
 */
import { LIFECYCLE_STATUS } from "../types/common";

export const UPLOAD_BLOCKED_STATUSES = new Set([
  LIFECYCLE_STATUS.APPROVED,
  LIFECYCLE_STATUS.EXPORT_READY,
  LIFECYCLE_STATUS.SENT_TO_D365,
  LIFECYCLE_STATUS.D365_FAILED,
  LIFECYCLE_STATUS.EXPORTED,
  "approved", // v1 fallback (invoice_status.state)
]);

/**
 * Returns true when the invoice has reached a status where re-uploading an
 * invoice copy is no longer permitted.
 *
 * Checks `lifecycle_status` first (v2 invoices); falls back to
 * `invoice_status.state` for v1 invoices that have not yet been migrated.
 */
export function isUploadBlocked(invoice: {
  lifecycle_status?: string | null;
  invoice_status?: { state?: string } | null;
}): boolean {
  const status = invoice.lifecycle_status || invoice.invoice_status?.state;
  if (!status) return false;
  return UPLOAD_BLOCKED_STATUSES.has(status);
}
